import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaSubscript,
  FaSuperscript,
  FaLink,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaListUl,
  FaListOl,
  FaQuoteLeft,
  FaCode,
  FaHighlighter,
  FaTable,
  FaRemoveFormat,
  FaChevronDown,
  FaCheckSquare,
  FaPlus,
  FaTrash,
  FaArrowRight,
} from "react-icons/fa";
import {
  MdFormatColorText,
  MdHorizontalRule,
  MdImage,
  MdCode,
} from "react-icons/md";
import { BiUndo, BiRedo } from "react-icons/bi";

const RTEToolbar = ({ editor, handleImageUpload }) => {
  const [headingDropdownOpen, setHeadingDropdownOpen] = useState(false);
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const [fontSizeDropdownOpen, setFontSizeDropdownOpen] = useState(false);
  const [textCaseDropdownOpen, setTextCaseDropdownOpen] = useState(false);
  const [tableDropdownOpen, setTableDropdownOpen] = useState(false);
  const textColorDropdownRef = useRef(null);
  const highlightDropdownRef = useRef(null);

  // Helper function to check if an image is currently selected
  const isImageSelected = useCallback(() => {
    if (!editor) return false;
    const { state } = editor;
    const { selection } = state;
    
    // Check NodeSelection (when image is directly selected via click)
    if (selection.node && selection.node.type.name === 'image') {
      return true;
    }
    
    // Check if cursor is positioned on/in an image node
    const { $from } = selection;
    const node = $from.node($from.depth);
    if (node.type.name === 'image') {
      return true;
    }
    
    // Check parent nodes
    for (let depth = $from.depth - 1; depth > 0; depth--) {
      const parentNode = $from.node(depth);
      if (parentNode.type.name === 'image') {
        return true;
      }
    }
    
    return false;
  }, [editor]);

  // Helper function to get the selected image node
  const getSelectedImageNode = useCallback(() => {
    if (!editor) return null;
    const { state } = editor;
    const { selection } = state;
    
    // Check NodeSelection
    if (selection.node && selection.node.type.name === 'image') {
      return selection.node;
    }
    
    // Check current node
    const { $from } = selection;
    const node = $from.node($from.depth);
    if (node.type.name === 'image') {
      return node;
    }
    
    // Check parent nodes
    for (let depth = $from.depth - 1; depth > 0; depth--) {
      const parentNode = $from.node(depth);
      if (parentNode.type.name === 'image') {
        return parentNode;
      }
    }
    
    return null;
  }, [editor]);

  // Helper function to handle alignment for both text and images
  const handleAlignment = useCallback((alignment) => {
    if (!editor) return;
    
    if (isImageSelected()) {
      // Update image alignment using a custom command
      editor.chain().focus().command(({ tr, state }) => {
        const { selection } = state;
        let imagePos = null;
        let imageNode = null;
        
        // Check if selection is a NodeSelection (image directly selected via click)
        if (selection.node && selection.node.type.name === 'image') {
          imageNode = selection.node;
          imagePos = selection.$anchor.pos;
        } else {
          // Find the image node at cursor position
          const { $from } = selection;
          
          // Check current node
          const node = $from.node($from.depth);
          if (node.type.name === 'image') {
            imageNode = node;
            imagePos = $from.before($from.depth);
          } else {
            // Search for image in parent nodes
            for (let depth = $from.depth - 1; depth > 0; depth--) {
              const parent = $from.node(depth);
              if (parent.type.name === 'image') {
                imageNode = parent;
                imagePos = $from.before(depth);
                break;
              }
            }
          }
        }
        
        if (imagePos !== null && imageNode) {
          tr.setNodeMarkup(imagePos, undefined, {
            ...imageNode.attrs,
            align: alignment,
          });
        }
        
        return true;
      }).run();
    } else {
      // Use normal text alignment for paragraphs/headings
      editor.chain().focus().setTextAlign(alignment).run();
    }
  }, [editor, isImageSelected]);

  // Helper function to check if alignment is active (for both text and images)
  const isAlignmentActive = useCallback((alignment) => {
    if (!editor) return false;
    
    // Check if image is selected first
    const imageNode = getSelectedImageNode();
    if (imageNode) {
      return imageNode.attrs.align === alignment;
    }
    
    // Check text alignment
    return editor.isActive({ textAlign: alignment });
  }, [editor, getSelectedImageNode]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setHeadingDropdownOpen(false);
        setFontDropdownOpen(false);
        setFontSizeDropdownOpen(false);
        setTextCaseDropdownOpen(false);
        setTableDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Adjust dropdown position to stay within screen bounds
  const adjustDropdownPosition = useCallback((dropdownRef) => {
    if (!dropdownRef?.current) return;

    const dropdown = dropdownRef.current;
    const container = dropdown.closest(".dropdown-container");
    if (!container) return;

    const button = container.querySelector("button");
    if (!button) return;

    // Wait for dropdown to be visible
    setTimeout(() => {
      const buttonRect = button.getBoundingClientRect();
      const dropdownRect = dropdown.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const padding = 10; // Padding from screen edge

      // Reset styles
      dropdown.style.left = "";
      dropdown.style.right = "";

      // Check if dropdown would overflow on the right
      if (buttonRect.left + dropdownRect.width > viewportWidth - padding) {
        // Align to right edge of button
        dropdown.style.right = "0";
        dropdown.style.left = "auto";
      } else {
        // Default: align to left edge
        dropdown.style.left = "0";
        dropdown.style.right = "auto";
      }
    }, 10);
  }, []);

  // Text case transformation functions
  const transformTextCase = (caseType) => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    if (from === to) return; // No selection

    const selectedText = editor.state.doc.textBetween(from, to);
    let transformedText = "";

    switch (caseType) {
      case "uppercase":
        transformedText = selectedText.toUpperCase();
        break;
      case "lowercase":
        transformedText = selectedText.toLowerCase();
        break;
      case "capitalize":
        transformedText = selectedText.replace(/\b\w/g, (char) =>
          char.toUpperCase()
        );
        break;
      default:
        return;
    }

    editor
      .chain()
      .focus()
      .deleteSelection()
      .insertContent(transformedText)
      .setTextSelection({ from, to: from + transformedText.length })
      .run();

    setTextCaseDropdownOpen(false);
  };

  if (!editor) {
    return null;
  }

  // Get current heading level
  const currentHeading = editor.isActive("heading", { level: 1 })
    ? "H1"
    : editor.isActive("heading", { level: 2 })
    ? "H2"
    : editor.isActive("heading", { level: 3 })
    ? "H3"
    : editor.isActive("heading", { level: 4 })
    ? "H4"
    : editor.isActive("heading", { level: 5 })
    ? "H5"
    : editor.isActive("heading", { level: 6 })
    ? "H6"
    : editor.isActive("paragraph")
    ? "Paragraph"
    : "Paragraph";

  return (
    <div
      className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
      style={{ overflow: "visible", position: "relative" }}
    >
      {/* Font Family Dropdown */}
      <div className="relative dropdown-container">
        <button
          onClick={() => {
            setFontDropdownOpen(!fontDropdownOpen);
            setHeadingDropdownOpen(false);
            setTextCaseDropdownOpen(false);
          }}
          className="px-3 py-1.5 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          title="Font"
        >
          <span className="text-gray-700 dark:text-gray-300 text-xs font-medium">
            Font
          </span>
          <FaChevronDown className="w-3 h-3 text-gray-500 dark:text-gray-400" />
        </button>
        {fontDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 min-w-[160px] py-1">
            {[
              "Arial",
              "Helvetica",
              "Times New Roman",
              "Courier New",
              "Verdana",
              "Georgia",
            ].map((font) => (
              <button
                key={font}
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .setFontFamily(font)
                    .run();
                  setFontDropdownOpen(false);
                }}
                onMouseDown={(e) => e.preventDefault()}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                style={{ fontFamily: font }}
              >
                {font}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Font Size Dropdown */}
      <div className="relative dropdown-container">
        <button
          onClick={() => {
            setFontSizeDropdownOpen(!fontSizeDropdownOpen);
            setFontDropdownOpen(false);
            setHeadingDropdownOpen(false);
            setTextCaseDropdownOpen(false);
          }}
          className="px-3 py-1.5 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          title="Font Size"
        >
          <span className="text-gray-700 dark:text-gray-300 text-xs font-medium">
            {editor.getAttributes("textStyle").fontSize || "12px"}
          </span>
          <FaChevronDown className="w-3 h-3 text-gray-500 dark:text-gray-400" />
        </button>
        {fontSizeDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 min-w-[120px] py-1">
            {[
              "8px",
              "10px",
              "12px",
              "14px",
              "16px",
              "18px",
              "20px",
              "24px",
              "28px",
              "32px",
              "36px",
              "48px",
            ].map((size) => (
              <button
                key={size}
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .setFontSize(size)
                    .run();
                  setFontSizeDropdownOpen(false);
                }}
                onMouseDown={(e) => e.preventDefault()}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  editor.getAttributes("textStyle").fontSize === size
                    ? "bg-gray-100 dark:bg-gray-700 font-semibold"
                    : ""
                }`}
                style={{ fontSize: size }}
              >
                {size}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5" />

      {/* Heading Dropdown */}
      <div className="relative dropdown-container">
        <button
          onClick={() => {
            setHeadingDropdownOpen(!headingDropdownOpen);
            setFontDropdownOpen(false);
            setFontSizeDropdownOpen(false);
            setTextCaseDropdownOpen(false);
          }}
          className="px-3 py-1.5 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          title="Change to..."
        >
          <span className="text-gray-700 dark:text-gray-300 text-xs font-medium">
            {currentHeading}
          </span>
          <FaChevronDown className="w-3 h-3 text-gray-500 dark:text-gray-400" />
        </button>
        {headingDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 min-w-[160px] py-1">
            <button
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .setParagraph()
                  .run();
                setHeadingDropdownOpen(false);
              }}
              onMouseDown={(e) => e.preventDefault()}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                editor.isActive("paragraph")
                  ? "bg-gray-100 dark:bg-gray-700 font-semibold"
                  : ""
              }`}
            >
              Paragraph
            </button>
            {[1, 2, 3, 4, 5, 6].map((level) => (
              <button
                key={level}
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .toggleHeading({ level })
                    .run();
                  setHeadingDropdownOpen(false);
                }}
                onMouseDown={(e) => e.preventDefault()}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  editor.isActive("heading", { level })
                    ? "bg-gray-100 dark:bg-gray-700 font-semibold"
                    : ""
                }`}
                style={{ fontSize: `${24 - level * 2}px`, fontWeight: "bold" }}
              >
                Heading {level}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Text Case Dropdown */}
      <div className="relative dropdown-container">
        <button
          onClick={() => {
            setTextCaseDropdownOpen(!textCaseDropdownOpen);
            setFontDropdownOpen(false);
            setFontSizeDropdownOpen(false);
            setHeadingDropdownOpen(false);
          }}
          className="px-3 py-1.5 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          title="Text Case"
        >
          <span className="text-gray-700 dark:text-gray-300 text-xs font-medium">
            Tt
          </span>
          <FaChevronDown className="w-3 h-3 text-gray-500 dark:text-gray-400" />
        </button>
        {textCaseDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 min-w-[140px] py-1">
            <button
              onClick={() => transformTextCase("uppercase")}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              UPPERCASE
            </button>
            <button
              onClick={() => transformTextCase("lowercase")}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              lowercase
            </button>
            <button
              onClick={() => transformTextCase("capitalize")}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Capitalize
            </button>
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5" />

      {/* Undo/Redo */}
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .undo()
            .run()
        }
        disabled={!editor.can().undo()}
        className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
        title="Undo"
      >
        <BiUndo className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .redo()
            .run()
        }
        disabled={!editor.can().redo()}
        className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
        title="Redo"
      >
        <BiRedo className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>

      <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5" />

      {/* Text Formatting */}
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleBold()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("bold") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Bold"
      >
        <FaBold className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleItalic()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("italic") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Italic"
      >
        <FaItalic className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleUnderline()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("underline") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Underline"
      >
        <FaUnderline className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleStrike()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("strike") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Strikethrough"
      >
        <FaStrikethrough className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleSubscript()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("subscript") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Subscript"
      >
        <FaSubscript className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleSuperscript()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("superscript") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Superscript"
      >
        <FaSuperscript className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>

      <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5" />

      {/* Lists */}
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleBulletList()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("bulletList") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Bullet List"
      >
        <FaListUl className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleOrderedList()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("orderedList") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Numbered List"
      >
        <FaListOl className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleTaskList()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("taskList") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Task List"
      >
        <FaCheckSquare className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleBlockquote()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("blockquote") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Blockquote"
      >
        <FaQuoteLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>

      <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5" />

      {/* Alignment */}
      <button
        onClick={() => handleAlignment("left")}
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          isAlignmentActive("left")
            ? "bg-gray-100 dark:bg-gray-700"
            : ""
        }`}
        title="Align Left"
      >
        <FaAlignLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() => handleAlignment("center")}
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          isAlignmentActive("center")
            ? "bg-gray-100 dark:bg-gray-700"
            : ""
        }`}
        title="Align Center"
      >
        <FaAlignCenter className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() => handleAlignment("right")}
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          isAlignmentActive("right")
            ? "bg-gray-100 dark:bg-gray-700"
            : ""
        }`}
        title="Align Right"
      >
        <FaAlignRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() => handleAlignment("justify")}
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          isAlignmentActive("justify")
            ? "bg-gray-100 dark:bg-gray-700"
            : ""
        }`}
        title="Justify"
      >
        <FaAlignJustify className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>

      <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5" />

      {/* Text Color & Highlight */}
      <div className="relative group dropdown-container">
        <button
          className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Text Color"
          onMouseEnter={() => {
            setTimeout(() => adjustDropdownPosition(textColorDropdownRef), 50);
          }}
        >
          <MdFormatColorText className="w-4 h-4 text-gray-700 dark:text-gray-300" />
        </button>
        <div
          ref={textColorDropdownRef}
          className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 hidden group-hover:block z-50 w-[220px]"
        >
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 px-1 uppercase tracking-wide">
            Text Color
          </div>
          <div className="grid grid-cols-6 gap-2.5">
            {[
              "#000000",
              "#FF0000",
              "#00FF00",
              "#0000FF",
              "#FFFF00",
              "#FF00FF",
              "#00FFFF",
              "#FFFFFF",
              "#808080",
              "#800000",
              "#008000",
              "#000080",
            ].map((color) => (
              <button
                key={color}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .setColor(color)
                    .run()
                }
                className="w-8 h-8 rounded-md border-2 border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:scale-110 hover:shadow-md transition-all cursor-pointer"
                style={{
                  backgroundColor: color,
                  boxShadow:
                    color === "#FFFFFF"
                      ? "inset 0 0 0 1px rgba(0,0,0,0.1)"
                      : "none",
                }}
                title={color}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="relative group dropdown-container">
        <button
          className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
            editor.isActive("highlight") ? "bg-gray-100 dark:bg-gray-700" : ""
          }`}
          title="Highlight"
          onMouseEnter={() => {
            setTimeout(() => adjustDropdownPosition(highlightDropdownRef), 50);
          }}
        >
          <FaHighlighter className="w-4 h-4 text-gray-700 dark:text-gray-300" />
        </button>
        <div
          ref={highlightDropdownRef}
          className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 hidden group-hover:block z-50 w-[220px]"
        >
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3 px-1 uppercase tracking-wide">
            Highlight Color
          </div>
          <div className="grid grid-cols-6 gap-2.5">
            {[
              "#FFFF00",
              "#FF0000",
              "#00FF00",
              "#0000FF",
              "#FF00FF",
              "#00FFFF",
              "#FFA500",
              "#FFC0CB",
              "#90EE90",
              "#ADD8E6",
              "#DDA0DD",
              "#F0E68C",
            ].map((color) => (
              <button
                key={color}
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .toggleHighlight({ color })
                    .run()
                }
                className="w-8 h-8 rounded-md border-2 border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:scale-110 hover:shadow-md transition-all cursor-pointer"
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5" />

      {/* Code */}
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleCode()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("code") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Inline Code"
      >
        <FaCode className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .toggleCodeBlock()
            .run()
        }
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("codeBlock") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Code Block"
      >
        <MdCode className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>

      {/* Link */}
      <button
        onClick={() => {
          const url = window.prompt("Enter URL:");
          if (url) {
            editor
              .chain()
              .focus()
              .setLink({ href: url })
              .run();
          }
        }}
        className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          editor.isActive("link") ? "bg-gray-100 dark:bg-gray-700" : ""
        }`}
        title="Insert Link"
      >
        <FaLink className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
      {editor.isActive("link") && (
        <button
          onClick={() =>
            editor
              .chain()
              .focus()
              .unsetLink()
              .run()
          }
          className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Remove Link"
        >
          <FaLink className="w-4 h-4 text-red-600 dark:text-red-400" />
        </button>
      )}

      {/* Horizontal Rule */}
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .setHorizontalRule()
            .run()
        }
        className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Horizontal Rule"
      >
        <MdHorizontalRule className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>

      {/* Image Upload */}
      <label className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors">
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleImageUpload(file);
            e.target.value = "";
          }}
        />
        <MdImage className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </label>

      {/* Table */}
      <div className="relative dropdown-container">
        <button
          onClick={() => {
            if (editor.isActive("table")) {
              setTableDropdownOpen(!tableDropdownOpen);
            } else {
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run();
            }
            setHeadingDropdownOpen(false);
            setFontDropdownOpen(false);
            setFontSizeDropdownOpen(false);
            setTextCaseDropdownOpen(false);
          }}
          className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
            editor.isActive("table") ? "bg-gray-100 dark:bg-gray-700" : ""
          }`}
          title={editor.isActive("table") ? "Table Options" : "Insert Table"}
        >
          <FaTable className="w-4 h-4 text-gray-700 dark:text-gray-300" />
        </button>
        {editor.isActive("table") && tableDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 py-1 min-w-[180px]">
            <button
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .addRowBefore()
                  .run();
                setTableDropdownOpen(false);
              }}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              title="Add Row Above"
            >
              <FaPlus className="w-3 h-3" />
              <span>Add Row Above</span>
            </button>
            <button
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .addRowAfter()
                  .run();
                setTableDropdownOpen(false);
              }}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              title="Add Row Below"
            >
              <FaPlus className="w-3 h-3" />
              <span>Add Row Below</span>
            </button>
            <button
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .addColumnBefore()
                  .run();
                setTableDropdownOpen(false);
              }}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              title="Add Column Left"
            >
              <FaArrowRight className="w-3 h-3 rotate-180" />
              <span>Add Column Left</span>
            </button>
            <button
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .addColumnAfter()
                  .run();
                setTableDropdownOpen(false);
              }}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              title="Add Column Right"
            >
              <FaArrowRight className="w-3 h-3" />
              <span>Add Column Right</span>
            </button>
            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
            <button
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .deleteRow()
                  .run();
                setTableDropdownOpen(false);
              }}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 text-red-600 dark:text-red-400"
              title="Delete Row"
            >
              <FaTrash className="w-3 h-3" />
              <span>Delete Row</span>
            </button>
            <button
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .deleteColumn()
                  .run();
                setTableDropdownOpen(false);
              }}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 text-red-600 dark:text-red-400"
              title="Delete Column"
            >
              <FaTrash className="w-3 h-3" />
              <span>Delete Column</span>
            </button>
            <button
              onClick={() => {
                editor
                  .chain()
                  .focus()
                  .deleteTable()
                  .run();
                setTableDropdownOpen(false);
              }}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 text-red-600 dark:text-red-400"
              title="Delete Table"
            >
              <FaTrash className="w-3 h-3" />
              <span>Delete Table</span>
            </button>
          </div>
        )}
      </div>

      <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-0.5" />

      {/* Clear Formatting */}
      <button
        onClick={() =>
          editor
            .chain()
            .focus()
            .clearNodes()
            .unsetAllMarks()
            .run()
        }
        className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Clear Formatting"
      >
        <FaRemoveFormat className="w-4 h-4 text-gray-700 dark:text-gray-300" />
      </button>
    </div>
  );
};

export default RTEToolbar;
